import React, { useContext, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { useLocalStorage } from 'usehooks-ts';
import { API_URL } from './ActionUrl';
import { newTheme } from '../components/theme/theme';
import { useThemeSettings } from '../layouts/state/useThemeSettings';
import { AuthContext } from './Auth';

interface DebugSettings {
  overrideApiUrl: string;
  storeOverride: string;
}

export default function DebugDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [overrideApiUrl, setOverrideApiUrl] = useLocalStorage('overrideApiUrl', '', {
    serializer: (v) => v,
    deserializer: (v) => v,
  });
  const [overrideStore, setOverrideStore] = useLocalStorage('overrideStore', '', {
    serializer: (v) => v,
    deserializer: (v) => v,
  });

  const formContext = useForm<DebugSettings>({
    defaultValues: useMemo(
      () => ({
        overrideApiUrl: overrideApiUrl,
        storeOverride: overrideStore,
      }),
      [overrideApiUrl, overrideStore],
    ),
  });

  const { handleSubmit, reset } = formContext;

  useEffect(() => {
    reset({
      overrideApiUrl: overrideApiUrl,
      storeOverride: overrideStore,
    });
  }, [overrideApiUrl, overrideStore, reset]);

  const onSubmit = (data: DebugSettings) => {
    setOverrideApiUrl(data.overrideApiUrl);
    setOverrideStore(data.storeOverride);
    window.location.reload();
    onClose();
  };

  const { settingData } = useContext(AuthContext);
  const { themedefaultColor, themedefaultTextColor, defaultFont } = useThemeSettings({
    settingData,
  });

  return (
    <ThemeProvider
      theme={newTheme({ themedefaultColor, themedefaultTextColor, fontFamily: defaultFont })}
    >
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Debug</DialogTitle>
        <DialogContent>
          <FormContainer formContext={formContext}>
            <Stack spacing={2}>
              <Typography variant="body1">Default API Url: {API_URL}</Typography>
              <TextFieldElement name="overrideApiUrl" label="Override API URL" />
              <TextFieldElement name="storeOverride" label="Override Store" />
            </Stack>
          </FormContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button onClick={handleSubmit(onSubmit)}>Save</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
